import { isAxiosError } from 'axios';
import HTTPResponse from '../HttpResponse';
import HttpClient from '../HttpClient';
import {
  Question,
  PostAnswerPayload,
  Answer,
  FileAnswer,
  PdfConversionMetadata,
} from './model';
import { Errors } from '../Appointment/errors';
import { isFileAnswer } from '../../helpers/questionsHelper';

class AdditionalInfoService {
  private httpBoka: HttpClient;

  constructor(httpBoka: HttpClient) {
    this.httpBoka = httpBoka;
  }

  public getQuestions = async (
    questionToken?: string,
  ): Promise<HTTPResponse<Question[]>> => {
    let res: Question[] = [];
    try {
      const result = await this.httpBoka.get<Question[]>(
        `/appointment/questions/${questionToken}`,
      );
      res = result.data;
      return new HTTPResponse(res);
    } catch (err) {
      if (isAxiosError(err)) {
        const { response } = err;
        return new HTTPResponse(res, response);
      }
      return new HTTPResponse(res);
    }
  };

  public getMandatoryQuestions = async (
    serviceId: string,
    retryOnce = true,
  ): Promise<HTTPResponse<Question[]>> => {
    try {
      const result = await this.httpBoka.get<Question[]>(
        `/services/questions/${serviceId}`,
      );
      return new HTTPResponse(result.data);
    } catch (err) {
      if (retryOnce) {
        return this.getMandatoryQuestions(serviceId, false);
      }
      return new HTTPResponse([]);
    }
  };

  public postAnswers = async (
    questionToken: string,
    answers: Answer[],
    orderNumber: string,
  ): Promise<HTTPResponse<PostAnswerPayload[]>> => {
    let res: PostAnswerPayload[] = [];

    const getAnswerPayload = (
      allAnswers: Answer[],
    ): Omit<Answer, 'questionType'>[] => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return allAnswers.map(({ questionType, ...rest }) => rest);
    };

    const fileAnswers = answers.filter((answer) => isFileAnswer(answer));
    const nonFileAnswers = answers.filter((answer) => !isFileAnswer(answer));
    const fileAnswerPayload = getAnswerPayload(fileAnswers);
    const nonFileAnswerPayload = getAnswerPayload(nonFileAnswers);
    const hasConsent = answers.some((answer) => answer.hasConsent);

    try {
      if (fileAnswerPayload.length > 0) {
        await this.postFileAnswers(
          questionToken,
          fileAnswerPayload,
          orderNumber,
          !!hasConsent,
        );
      }

      if (nonFileAnswerPayload.length > 0) {
        const result = await this.httpBoka.post<PostAnswerPayload[]>(
          `/appointment/answers/${questionToken}`,
          nonFileAnswerPayload,
        );
        res = result.data;
      }

      return new HTTPResponse(res);
    } catch (err) {
      if (isAxiosError(err)) {
        const { response } = err;
        if (response) {
          return new HTTPResponse(res, response);
        } else {
          return new HTTPResponse(res, Errors.SERVICE_NOT_AVAILABLE);
        }
      }
      return new HTTPResponse(res, Errors.SERVICE_NOT_AVAILABLE);
    }
  };

  private postFileAnswers = async (
    questionToken: string,
    fileAnswers: PostAnswerPayload[],
    orderNumber: string,
    hasConsent?: boolean,
  ): Promise<void> => {
    if (!hasConsent) {
      return;
    }

    const orderNo = orderNumber || null;

    const formData = new FormData();
    fileAnswers.forEach((fileAnswer) => {
      (fileAnswer.answer as FileAnswer[]).forEach((file) => {
        formData.append(fileAnswer.questionId.toString(), file);

        // Append metadata if any of the properties exist
        const metadata: Partial<PdfConversionMetadata> = {};
        if (file.pdfMetadata?.originalType) {
          metadata.originalType = file.pdfMetadata.originalType;
        }
        if (file.pdfMetadata?.originalFileName) {
          metadata.originalFileName = file.pdfMetadata.originalFileName;
        }
        if (file.pdfMetadata?.pageNumber !== undefined) {
          metadata.pageNumber = file.pdfMetadata.pageNumber;
        }

        // Append metadata as individual form fields if we have any metadata
        if (Object.keys(metadata).length > 0) {
          formData.append(
            `${fileAnswer.questionId.toString()}_metadata`,
            JSON.stringify(metadata),
          );
        }
      });
    });

    try {
      await this.httpBoka.post(
        `/api/answers/uploadFile?token=${questionToken}&orderNumber=${orderNo}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-user-file-consent': 'true',
          },
        },
      );
    } catch (err) {}
  };
}

export default AdditionalInfoService;
